import mergeWith from 'lodash.mergewith';
import type ItineraryPackage from '@itinerary/ItineraryPackage';
import { GeneralSet } from '#imports';
import {
    type FilterData,
    type FilterKey,
    LoadingStatus,
    type MetaData,
} from '@itinerary/ItineraryModuleTypes';
import type ItineraryPackageLoader from '@itinerary/ItineraryPackageLoader';
import { type ItineraryPackageLoaderResult } from '@itinerary/ItineraryPackageLoader';
import { RequestCanceledException } from '@utils/itinerary';
import { type ItineraryPackageLoaderConfig } from '@itinerary/types';

/**
 * Stores data related to a list of packages, that one of them must be chosen
 */
export default abstract class ItineraryPackageSet<
    TFilterMetaData extends FilterData = FilterData,
    TFilterKey extends FilterKey = FilterKey,
    TPackage extends ItineraryPackage = ItineraryPackage,
> {
    /**
     * The package loader associated with this set
     */
    protected abstract loader: ItineraryPackageLoader<TPackage>;
    /**
     * The list of packages
     */
    protected get _list(): TPackage[] {
        return this._set.toArray();
    }

    protected _set: GeneralSet<TPackage> = new GeneralSet<TPackage>()
        .setCompareKey('uniqueId')
        .setPriorityOrder('new');

    public get list() {
        return this._list;
    }

    /**
     * Loading status of the list
     */
    loadingStatus: LoadingStatus = LoadingStatus.None;
    /**
     * Meta data used to render filters for the list
     */
    filterMetaData: TFilterMetaData | null = null;

    metaData: MetaData = {};

    /**
     * Stores the current sorting method
     */
    abstract sortBy: any;

    isSuggestion = false;

    reinitialize(
        config: ItineraryPackageLoaderConfig,
        filters: Record<TFilterKey, any>,
        minPrice?: number
    ) {
        if (this.loader.compareTo(config)) {
            return;
        }
        this.reset(config, filters, minPrice);
    }

    protected reset(
        _config: ItineraryPackageLoaderConfig,
        filters: Record<TFilterKey, any>,
        minPrice?: number
    ) {
        this.loader.stopLoading();
        this.filters = filters;
        this.minPrice = minPrice;
        this._set = this._set.clone().clean();
        this.loadingStatus = LoadingStatus.None;
        this.filterMetaData = null;
        this.metaData = {};
        this.isSuggestion = false;
    }

    get isLoaded() {
        return (
            this.loadingStatus !== LoadingStatus.None && !this.loader.needsLoad
        );
    }

    get needsReload() {
        return (
            this.loadingStatus === LoadingStatus.Success &&
            this.loader.needsReload
        );
    }

    get url() {
        return this.loader.url;
    }

    get date() {
        return this.loader.date;
    }

    onGenerate(_result: ItineraryPackageLoaderResult<TPackage>) {}

    async loadAvailable() {
        this.loadingStatus = LoadingStatus.Loading;
        this._set = this._set.clone().clean();
        try {
            const loaderGenerator = this.loader.load();
            for await (const result of loaderGenerator ?? []) {
                if (result) {
                    if (this.loader.isRealTime) {
                        this.loadingStatus = LoadingStatus.RealtimeLoading;
                    }
                    this._set = this._set.clone().addAll(result.list);
                }
                this.filterMetaData = mergeWith(
                    this.filterMetaData,
                    result.filterMetaData
                );
                this.metaData = {
                    hasPreSell: result.metaData?.hasPreSell,
                    routeNotFound: result.metaData?.routeNotFound,
                    priceTrackEnabled: result.metaData?.priceTrackEnabled,
                };
                this.onGenerate(result);
            }
            this.loadingStatus = LoadingStatus.Success;
        } catch (e) {
            if (e instanceof RequestCanceledException) {
                if (e.requestAlreadyLoaded) {
                    this.loadingStatus = LoadingStatus.Success;
                }
                return;
            }
            this.loadingStatus = LoadingStatus.Error;
            throw e;
        }
    }

    resetLastLoad() {
        this.loader.resetLoadTime();
    }

    stop() {
        this.loader.stopLoading();
    }

    resetList() {
        this._set = this._set.clone().clean();
    }

    handleChange() {
        this.stop();
        this.loadingStatus = LoadingStatus.Loading;
        this.resetList();
    }

    protected constructor(
        /**
         * Contains filters that must be applied to the results
         */
        public filters: Record<TFilterKey, any>,

        /**
         * Minimum price that is obtained or expected from the current package set
         */
        public minPrice?: number
    ) {}
}
